import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AdminSession, Session, SessionStatus } from '@core/models/Session';
import { Unit } from '@core/models/Unit';
import { RoutesV3Service } from '@core/services/entities-services/routes-v3.service';
import { StorageService } from '@core/services/storage.service';
import { AlertController } from '@ionic/angular';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
const sateDefault: Session = {
  jwt: null,
  driver: null,
  unit: null,
  services: null,
  sid: null,
  parent: null,
  policyChecked: null,
  user: null,
  platformAccount: null,
};
const sateAdminDefault: AdminSession = {
  jwt: null,
  services: null,
  sid: null,
  parent: null,
  user: null,
  policyChecked: null,
  platformToken: null,
};
@Injectable({ providedIn: 'root' })
export class SessionService {
  protected readonly _adminSession = new BehaviorSubject<AdminSession>(sateAdminDefault);
  protected readonly _session = new BehaviorSubject<Session>(sateDefault);
  readonly session: Observable<Session> = this._session.asObservable();
  protected readonly _loading = new BehaviorSubject<boolean>(true);
  readonly loading = this._loading.asObservable();
  protected readonly _logoutEvent = new Subject<void>();
  readonly logout$ = this._logoutEvent.asObservable();
  private intervalStatusUnitID: any = null;
  private authUrl = environment.authUrl.url;
  private readonly _versions: BehaviorSubject<{ android: number, androidName: string, ios: string }> = new BehaviorSubject<{ android: number; androidName: string, ios: string }>({
    android: 1000,
    androidName: '100.59.16',
    ios: '1.57.29',
  });
  readonly versions = this._versions.asObservable();
  constructor(
    private _api: ApiService,
    private _storageService: StorageService,
    private _alert: AlertController,
    private _http: HttpClient,
  ) {
  }
  getSession(): Session {
    return this._session.getValue();
  }
  getAdminSession(): AdminSession {
    return this._adminSession.getValue();
  }
  updateSession(session: Session) {
    this._session.next(session);
  }
  setLoading(loading: boolean) {
    this._loading.next(loading);
  }
  loginWithPassword(phone: string, password: string): Promise<boolean> {
    return new Promise((resolve) => {
      this._http.post<{ access: string, refresh: string }>(`${this.authUrl}/auth/login/phone`, { phone, password }).subscribe({
        next: async (loginResponse) => {
          await this._storageService.set('jwt', loginResponse.access);
          await this._storageService.set('refresh', loginResponse.refresh);
          await this.loadSession(false);
          return resolve(true);
        },
      });
    });
  }
  async loadAdminSession(showLoader = false) {
    try {
      const appInfo = await App.getInfo();
      this._versions.next({ android: +appInfo.build, androidName: appInfo.version, ios: appInfo.version });
    } catch (e) {
      console.log('No se pudo verificar la version');
    }
    let session = this._adminSession.getValue();
    if (!session.platformToken || !session.services || !session.parent || !session.sid) {
      if (showLoader) {
        this._loading.next(true);
      }
      const platformToken = await this._storageService.get('platformToken');
      if (platformToken) {
        session = await lastValueFrom(this._http.post<AdminSession>(`${this.authUrl}/auth/session/validate/token`, { token: platformToken }));
        session = { ...session, policyChecked: true, platformToken: platformToken, services: session.services };
      }
      if (showLoader) {
        this._loading.next(false);
      }
    }
    this._adminSession.next(session);
    return session;
  }
  async loginWithToken(token: string): Promise<boolean> {
    try {
      const loginResponse = await lastValueFrom(this._http.post<{ access: string, refresh: string }>(`${this.authUrl}/auth/login/token`, { token }));
      await this._storageService.set('platformToken', token);
      await this._storageService.set('jwt', loginResponse.access);
      await this._storageService.set('refresh', loginResponse.refresh);
      await this.loadAdminSession(false);
      return true;
    } catch (e) {
      return false;
    }
  }
  async getUnit(): Promise<Unit> {
    try {
      let unit = JSON.parse(await this._storageService.get('unit'));
      if (!unit) {
        unit = await lastValueFrom(this._api.get('/home/unit', false));
        await this._storageService.set('unit', JSON.stringify(unit));
      }
      return unit;
    } catch (e) {
      const alert = await this._alert.create({
        header: 'Error al consultar la unidad vinculada',
        message: 'Por favor vuelva a ingresar',
        buttons: [{ text: 'Ok' }],
      });
      await alert.present();
      return null;
    }
  }
  async bindUnit(unit: any) {
    await this._storageService.set('unit', JSON.stringify(unit));
    this._session.next({ ...this._session.getValue(), unit: unit });
  }
  async unbindUnit() {
    await this._storageService.remove('unit');
    this._session.next({ ...this._session.getValue(), unit: null });
  }
  async loadSession(showLoader = true): Promise<Session> {
    if (Capacitor.getPlatform() !== 'web') {
      try {
        const appInfo = await App.getInfo();
        this._versions.next({ android: +appInfo.build, androidName: appInfo.version, ios: appInfo.version });
      } catch (e) {
        console.log('No se pudo verificar la version');
      }
    }
    let session = this._session.getValue();
    if (!session.jwt || !session.services || !session.driver || !session.parent || !session.sid) {
      if (showLoader) {
        this._loading.next(true);
      }
      const jwt = await this._storageService.get('jwt');
      const platformToken = await this._storageService.get('platformToken');
      if (jwt && !platformToken) {
        session = await lastValueFrom(this._http.post<Session>(`${this.authUrl}/auth/session/validate`, { jwt }));
        session.unit = await lastValueFrom(this._api.get('/home/unit', false));
        session = { ...session, jwt, services: session.services };
      }
      if (showLoader) {
        this._loading.next(false);
      }
    }
    this._session.next(session);
    return session;
  }
  getSesisonByDriverId(driverId: number): Promise<SessionStatus> {
    return lastValueFrom(this._api.get(`/vinculate/driver/status/${driverId}`, false));
  }
  async logout(routesService?: RoutesV3Service): Promise<void> {
    try {
      await lastValueFrom(this._api.get('/login/logout', false));
      this._logoutEvent.next();
    } catch (e: any) {
      console.log('Error al hacer el logout');
    } finally {
      this.stopLoadBindingUnit();
      await this._storageService.clear();
      this._session.next(sateDefault);
      this._logoutEvent.next();
    }
  }
  stopLoadBindingUnit() {
    if (this.intervalStatusUnitID) {
      clearInterval(this.intervalStatusUnitID);
      this.intervalStatusUnitID = null;
    }
  }
  loadBindingUnit() {
    if (!this.intervalStatusUnitID) {
      this.intervalStatusUnitID = setInterval(async () => {
        const jwt = await this._storageService.get('jwt');
        if (jwt) {
          const vinculateStatus = await lastValueFrom(this._api.get('/vinculate/status', false));
          const currentUnit = this._session.getValue()?.unit;
          if (vinculateStatus.unit) {
            if (!currentUnit || currentUnit.id !== vinculateStatus.unit.id) {
              await this.bindUnit(vinculateStatus.unit);
            }
          } else if (currentUnit) {
            await this.unbindUnit();
          }
        }
      }, 10000);
    }
  }
}
